import { css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LitPage } from '../lit-page';
import { devWarn, isNavTarget, type NavTarget } from '../luna-common';

const NAV_ITEMS: ReadonlyArray<{ target: NavTarget; label: string }> = [
  { target: 'home', label: 'Home' },
  // { target: 'programme', label: 'My programme' },
  { target: 'appearance', label: 'Appearance' }, /* [DESIGNER REVIEW] — figma 26:9 drew two items */
];

@customElement('luna-nav')
export class LunaNav extends LitPage {
  @property({ attribute: false }) items: NavTarget[] = ['home', 'programme'];

  private _current: NavTarget = 'home';

  @property({ attribute: 'current' })
  get current(): NavTarget {
    return this._current;
  }

  set current(value: string | null) {
    const next = String(value ?? '');
    if (!isNavTarget(next)) {
      devWarn(`luna-nav: unknown current "${next}" — falling back to "home"`);
      this._current = 'home';
      return;
    }
    this._current = next;
  }

  private emitNavigate(target: NavTarget): void {
    this.dispatchEvent(
      new CustomEvent('luna-navigate', {
        detail: { target },
        bubbles: true,
        composed: true,
      })
    );
  }

  private visibleItems(): ReadonlyArray<{ target: NavTarget; label: string }> {
    const result: { target: NavTarget; label: string }[] = [];
    for (const target of this.items) {
      const item = NAV_ITEMS.find((entry) => entry.target === target);
      if (!item) {
        devWarn(`luna-nav: unknown target "${target}" — dropping`);
        continue;
      }
      result.push(item);
    }
    return result;
  }

  content() {
    return html`
      <nav class="luna-nav" aria-label="Main navigation">
        ${this.visibleItems().map(
          (item) => html`
            <button
              type="button"
              class="luna-nav__item ${item.target === this._current ? 'is-current' : ''}"
              aria-current=${item.target === this._current ? 'page' : undefined}
              @click=${() => this.emitNavigate(item.target)}
            >
              <span class="mbw-type-label-nav">${item.label}</span>
            </button>
          `
        )}
      </nav>
    `;
  }

  styles() {
    return css`
      .luna-nav {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 8px;
      }

      .luna-nav__item {
        height: 46px;
        box-sizing: border-box;
        padding: 0 20px;
        border: none;
        border-radius: var(--luna-radius-btn-sm);
        background: transparent;
        cursor: pointer;
        color: var(--mbw-onbar-text);
        opacity: 0.9; /* pinned: figma Nav Item (default label) */
      }

      .luna-nav__item:hover {
        background: rgba(255, 255, 255, 0.14); /* pinned: figma Nav Item */
        opacity: 1;
      }

      .luna-nav__item.is-current {
        background: var(--mbw-onbar-pill);
        opacity: 1;
      }

      .luna-nav__item:focus-visible {
        outline: none;
        box-shadow: 0 0 0 5px #ffffff, 0 0 0 7px var(--cds-focus);
      }
    `;
  }
}
